import React from "react";
import {
  Box,
  Typography,
  Divider,
  TextField,
  Chip,
  Button,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AddIcon from "@mui/icons-material/Add";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  pointerWithin,
  DragStartEvent,
  DragEndEvent,
} from "@dnd-kit/core";
import { ExpensePriority } from "../../lib/types";
import { formatCurrency } from "../../lib/expenseUtils";
import {
  RecurringBillLine,
  occurrencesPerMonth,
} from "../../lib/recurringBillsUtils";
import RecurringBillRow from "./RecurringBillRow";

interface RecurringPriorityBoardProps {
  // Only "bill" lines are rankable; debts have no numeric priority.
  lines: RecurringBillLine[];
  onSetRank: (expenseId: string, rank: number | null) => void;
  // Hide-from-totals state, shared with the parent dialog.
  excluded: Set<string>;
  onToggleExcluded: (id: string) => void;
  showPostpone: boolean;
  onRequestPostpone?: (line: RecurringBillLine, anchorEl: HTMLElement) => void;
  onSetExpensePriority?: (
    expenseId: string,
    priority: ExpensePriority | null
  ) => void;
  onEditBill?: (id: string) => void;
  onDeleteBill?: (id: string) => void;
  onEditDebt?: (debtId: string) => void;
  onDeleteDebt?: (debtId: string) => void;
}

const UNRANKED = "unranked";
const NEW_GROUP = "new-group";

const monthlyOf = (l: RecurringBillLine) =>
  l.amount * occurrencesPerMonth(l.frequency);

type RowHandlers = Omit<RecurringPriorityBoardProps, "lines" | "onSetRank">;

// One draggable expense row — the full-feature shared row plus a drag handle.
const BillRow: React.FC<
  {
    line: RecurringBillLine;
    dragging?: boolean;
  } & RowHandlers
> = ({ line, dragging, excluded, ...handlers }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: line.id,
  });
  const dragHandle = (
    <Box
      {...attributes}
      {...listeners}
      sx={{
        display: "flex",
        alignItems: "center",
        cursor: "grab",
        color: "text.disabled",
        touchAction: "none",
      }}
    >
      <DragIndicatorIcon fontSize="small" />
    </Box>
  );
  return (
    <Box
      ref={setNodeRef}
      sx={{
        borderRadius: 1,
        px: 0.5,
        bgcolor: dragging ? "background.paper" : "transparent",
        boxShadow: dragging ? 4 : 0,
        opacity: isDragging ? 0.4 : 1,
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <RecurringBillRow
        line={line}
        displayAmount={monthlyOf(line)}
        hidden={excluded.has(line.id)}
        dragHandle={dragHandle}
        {...handlers}
      />
    </Box>
  );
};

// A droppable priority bucket. Everything dropped into it adopts its rank.
const Bucket: React.FC<{
  droppableId: string;
  header: React.ReactNode;
  children: React.ReactNode;
  dashed?: boolean;
}> = ({ droppableId, header, children, dashed }) => {
  const { setNodeRef, isOver } = useDroppable({ id: droppableId });
  return (
    <Box
      ref={setNodeRef}
      sx={{
        mb: 1.5,
        p: 1,
        borderRadius: 2,
        border: "1px solid",
        borderStyle: dashed ? "dashed" : "solid",
        borderColor: isOver ? "primary.main" : "divider",
        bgcolor: isOver ? "action.selected" : "transparent",
        transition: "background-color 120ms, border-color 120ms",
      }}
    >
      {header}
      <Box sx={{ mt: 0.5 }}>{children}</Box>
    </Box>
  );
};

const RecurringPriorityBoard: React.FC<RecurringPriorityBoardProps> = ({
  lines,
  onSetRank,
  ...rowHandlers
}) => {
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const bills = lines.filter((l) => l.kind === "bill");

  // Group by rank. Ranked buckets sorted ascending; unranked collected separately.
  const rankedMap = new Map<number, RecurringBillLine[]>();
  const unranked: RecurringBillLine[] = [];
  for (const l of bills) {
    if (l.priorityRank == null) unranked.push(l);
    else {
      const arr = rankedMap.get(l.priorityRank) || [];
      arr.push(l);
      rankedMap.set(l.priorityRank, arr);
    }
  }
  const rankKeys = Array.from(rankedMap.keys()).sort((a, b) => a - b);
  const maxRank = rankKeys.length ? rankKeys[rankKeys.length - 1] : 0;

  const activeLine = activeId
    ? bills.find((l) => l.id === activeId) || null
    : null;

  const handleDragStart = (e: DragStartEvent) =>
    setActiveId(String(e.active.id));

  const handleDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over) return;
    const id = String(active.id);
    const target = String(over.id);
    if (target === UNRANKED) {
      onSetRank(id, null);
    } else if (target === NEW_GROUP) {
      onSetRank(id, maxRank + 1);
    } else if (target.startsWith("rank-")) {
      const n = Number(target.slice("rank-".length));
      if (!Number.isNaN(n)) onSetRank(id, n);
    }
  };

  // Re-number every item in a bucket to a new literal number (merges if it
  // collides with an existing bucket).
  const renumberBucket = (from: number, to: number) => {
    if (to < 1 || to === from) return;
    (rankedMap.get(from) || []).forEach((l) => onSetRank(l.id, to));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {bills.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No recurring expenses to prioritize.
        </Typography>
      )}

      {rankKeys.map((rank) => {
        const items = rankedMap.get(rank) || [];
        const subtotal = items.reduce((s, l) => s + monthlyOf(l), 0);
        return (
          <Bucket
            key={rank}
            droppableId={`rank-${rank}`}
            header={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TextField
                    size="small"
                    type="number"
                    value={rank}
                    onChange={(e) => {
                      const n = Math.max(1, Math.round(Number(e.target.value)));
                      if (!Number.isNaN(n)) renumberBucket(rank, n);
                    }}
                    inputProps={{
                      min: 1,
                      style: {
                        width: 48,
                        padding: "4px 6px",
                        fontWeight: 700,
                      },
                    }}
                    sx={{ "& input": { textAlign: "center" } }}
                  />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Priority #{rank}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${items.length} ${items.length === 1 ? "item" : "items"}`}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {formatCurrency(subtotal)} / mo
                </Typography>
              </Box>
            }
          >
            <Divider sx={{ mb: 0.5 }} />
            {items.map((l) => (
              <BillRow key={l.id} line={l} {...rowHandlers} />
            ))}
          </Bucket>
        );
      })}

      <Bucket
        droppableId={NEW_GROUP}
        dashed
        header={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AddIcon fontSize="small" sx={{ color: "text.disabled" }} />
            <Typography variant="caption" color="text.secondary">
              Drop here for a new Priority #{maxRank + 1}
            </Typography>
          </Box>
        }
      >
        {null}
      </Bucket>

      <Bucket
        droppableId={UNRANKED}
        header={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Unranked
            </Typography>
            <Chip size="small" label={`${unranked.length}`} />
            {unranked.length > 0 && (
              <Button
                size="small"
                sx={{ textTransform: "none", ml: "auto" }}
                onClick={() =>
                  unranked.forEach((l, i) => onSetRank(l.id, maxRank + 1 + i))
                }
              >
                Auto-number all
              </Button>
            )}
          </Box>
        }
      >
        <Divider sx={{ mb: 0.5 }} />
        {unranked.length === 0 ? (
          <Typography variant="caption" color="text.disabled">
            Everything has a priority.
          </Typography>
        ) : (
          unranked.map((l) => <BillRow key={l.id} line={l} {...rowHandlers} />)
        )}
      </Bucket>

      <DragOverlay>
        {activeLine ? (
          <BillRow line={activeLine} {...rowHandlers} dragging />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default RecurringPriorityBoard;
